import React, { forwardRef, useEffect, useState } from "react";
import { Trans } from "react-i18next";
import styled from "styled-components";

import {
    DialogActions,
    DialogContent,
    DialogTitle
} from "@material-ui/core";

import Analytics from "js/analytics";
import { Icon } from "js/Components/Icon";
import { BeautifulDialog } from "js/react/components/Dialogs/BaseDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { SubscriptionStatus } from "common/constants";
import { Button } from "js/Components/Button";
import BillingController, { BillingControllerState } from "js/controllers/BillingController";
import WorkspaceController from "js/controllers/WorkspaceController";
import getLogger, { LogGroup } from "js/core/logger";
import ErrorDialog from "js/react/components/Dialogs/ErrorDialog";
import { themeColors } from "js/react/sharedStyles";
import ChangePaymentMethodDialog from "js/react/views/Billing/dialogs/ChangePaymentMethodDialog";
import { ShowDialogAsync } from "js/react/components/Dialogs/BaseDialog";
import { PaymentRequiredError } from "apis/errors";
import * as StripeUtils from "common/utils/stripeUtils";
import { t } from "js/i18n";

const logger = getLogger(LogGroup.BILLING);

const ChargeInfoMessage = styled.span`
    margin-left: 12px;
    font-size: 14px;
`;

const StyledDialog = styled(BeautifulDialog)`
    .MuiDialog-paper {
        min-height: 305px; 
    }
`;

const CloseDialogButton = styled(Button)`
    &&& {
        margin-top: 20px;
        margin-bottom: 10px;
    }
`;

const SuccessfulPurchaseContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    color: #222;
    font-size: 23px;
    text-align: center;
    padding: 0px 20px;

    >div {
        width: 100%;
    }
`;

const SuccessfulPurchaseIconContainer = styled.div`
    .bai-icon {
        font-size: 70px;
        margin: 20px;
        transition: all 0.5s ease;
        animation: checked 1s normal forwards ease-in-out;
        animation-iteration-count: 1;
        animation-delay: 0s;
        clip-path: inset(0 100% 0 0);
    }
`;

const SuccessfulPurchaseMessage = styled.div`
    animation: fadein 1s normal forwards ease-in-out;
    margin-bottom: 50px;
`;

const SuccessfulPurchaseReceipt = styled.div`
    font-size: 18px;
    margin-bottom: 10px;
    color: #555;
`;

interface EndTrialDialogProps {
    closeDialog: (result: boolean) => void;
}

function EndTrialDialog(props: EndTrialDialogProps & BillingControllerState, ref: any) {
    const { closeDialog, stripeData: { subscription, upcomingInvoice } } = props;

    const [hasEndedTrial, setHasEndedTrial] = useState<boolean>(false);
    const [fetching, setFetching] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>(null);

    useEffect(() => {
        if (!subscription) {
            setErrorMessage(t("You do not have a subscription."));
            return;
        }
        if (subscription.status !== SubscriptionStatus.TRIALING) {
            setErrorMessage(t("Your trial has already ended."));
            return;
        }
        if (!upcomingInvoice) {
            setErrorMessage(t("Unable to get upcoming invoice."));
            return;
        }
    }, []);

    const handleEndTrial = async () => {
        setFetching(true);

        try {
            const days_to_trial_end = StripeUtils.Subscription.getTrialDaysRemaining(subscription);

            await BillingController.endTrialOnSubscription();

            const dialogWasShown = await WorkspaceController.checkPastDue();
            if (dialogWasShown) {
                closeDialog(true);
                return;
            }

            setHasEndedTrial(true);
            Analytics.ModifyWorkspacePlan.StartSubscriptionEarly({ days_to_trial_end });
        } catch (err) {
            if (err instanceof PaymentRequiredError) {
                if (await ShowDialogAsync(ChangePaymentMethodDialog)) {
                    handleEndTrial();
                    return;
                }

                closeDialog(false);
                return;
            }

            logger.error(err, "[EndTrialDialog] Error ending trial");
            setErrorMessage(t("Sorry, something went wrong. Please try again later."));
        } finally {
            setFetching(false);
        }
    };

    if (errorMessage) {
        return (<ErrorDialog
            ref={ref}
            // @ts-ignore
            preventClose
            title={t("Error")}
            message={errorMessage}
            closeDialog={() => closeDialog(false)}
        />);
    }

    const amountDue = (upcomingInvoice?.amount_due ?? 0) / 100;

    return (
        <StyledDialog preventClose ref={ref}>
            <FetchingClickShield visible={fetching} backgroundColor={"white"} />

            {!hasEndedTrial && <>
                <DialogTitle>
                    {t("Are you sure you want to end your trial now?")}
                </DialogTitle>
                <DialogContent>
                    <ChargeInfoMessage>
                        <Trans
                            i18nKey="You will be charged <bold>${{amount}}</bold> and will have access to unlimited AI credits immediately."
                            values={{ amount: amountDue.toFixed(2) }}
                            components={{ bold: <strong /> }}
                        />
                    </ChargeInfoMessage>
                </DialogContent>
                <DialogActions>
                    <Button unframed large onClick={() => closeDialog(false)}>
                        {t("Cancel")}
                    </Button>
                    <Button blue large onClick={handleEndTrial}>
                        {t("Upgrade now")}
                    </Button>
                </DialogActions>
            </>}

            {hasEndedTrial && <>
                <DialogContent>
                    <SuccessfulPurchaseContainer>
                        <SuccessfulPurchaseIconContainer>
                            <Icon color={themeColors.ui_blue}>check</Icon>
                        </SuccessfulPurchaseIconContainer>
                        <SuccessfulPurchaseMessage>
                            {t("You successfully started your subscription.")}
                        </SuccessfulPurchaseMessage>
                        <SuccessfulPurchaseReceipt>
                            {t("A receipt will be sent to your email shortly.")}
                        </SuccessfulPurchaseReceipt>
                        <CloseDialogButton
                            onClick={() => closeDialog(true)}
                            blue
                            large
                        >
                            {t("Close")}
                        </CloseDialogButton>
                    </SuccessfulPurchaseContainer>
                </DialogContent>
            </>}
        </StyledDialog >
    );
}

export default BillingController.withInitializedState(forwardRef(EndTrialDialog));
